import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { Mail } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { DataTable } from '@/components/DataTable'
import { EmptyValue } from '@/components/EmptyValue'
import { PageHeader } from '@/components/PageHeader'
import { PendingButton } from '@/components/PendingButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Textarea } from '@/components/ui/textarea'
import {
  listSupportMessages,
  type SupportMessageRow,
  setSupportMessageNote,
  setSupportMessageStatus,
} from '@/lib/support/server'
import {
  SUPPORT_TOPIC_LABELS,
  SUPPORT_TOPICS,
  type SupportTopic,
} from '@/lib/support/support'

export const Route = createFileRoute('/_authenticated/admin/support')({
  component: AdminSupportPage,
})

const QUERY_KEY = ['admin-support-messages'] as const

function formatWhen(value: Date | string): string {
  return new Date(value).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

/** Compact form for the table, where the year costs width the Open action
 *  needs. The full timestamp is in the detail sheet. */
function formatReceived(value: Date | string): string {
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Replies are sent from the shared mailbox, not the app — this is the same
 *  Reply-To the alert email carries, offered as a one-click fallback for anyone
 *  working from the admin page instead of their inbox. */
function replyMailto(row: SupportMessageRow): string {
  const subject = `Re: [${SUPPORT_TOPIC_LABELS[row.topic]}] your message to Masklabs support`
  return `mailto:${row.email}?${new URLSearchParams({ subject }).toString()}`
}

function StatusBadge({ status }: { status: SupportMessageRow['status'] }) {
  return (
    <Badge variant={status === 'new' ? 'default' : 'secondary'}>
      {status === 'new' ? 'New' : 'Handled'}
    </Badge>
  )
}

/** Sheet body for one message: the full text, the account context, the status
 *  toggle, and the internal note. */
function MessageDetail({
  row,
  onClose,
}: {
  row: SupportMessageRow
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState(row.internalNote ?? '')
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY })

  const toggleStatus = useMutation({
    mutationFn: () =>
      setSupportMessageStatus({
        data: { id: row.id, status: row.status === 'new' ? 'handled' : 'new' },
      }),
    onSuccess: () => {
      void invalidate()
      toast.success(row.status === 'new' ? 'Marked handled' : 'Reopened')
      onClose()
    },
    onError: (e) =>
      toast.error("Couldn't update the status", {
        description: (e as Error).message,
      }),
  })

  const saveNote = useMutation({
    mutationFn: () => setSupportMessageNote({ data: { id: row.id, note } }),
    onSuccess: () => {
      void invalidate()
      toast.success('Note saved')
    },
    onError: (e) =>
      toast.error("Couldn't save the note", {
        description: (e as Error).message,
      }),
  })

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={row.status} />
        <Badge variant="outline">{SUPPORT_TOPIC_LABELS[row.topic]}</Badge>
        <span className="text-muted-foreground text-xs">
          {formatWhen(row.createdAt)}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button asChild size="sm">
          <a href={replyMailto(row)}>
            <Mail data-icon="inline-start" />
            Reply by email
          </a>
        </Button>
        <PendingButton
          type="button"
          variant="outline"
          size="sm"
          pending={toggleStatus.isPending}
          onClick={() => toggleStatus.mutate()}
        >
          {row.status === 'new' ? 'Mark handled' : 'Reopen'}
        </PendingButton>
      </div>

      <div className="whitespace-pre-wrap rounded-md border bg-muted/30 p-4 text-sm">
        {row.message}
      </div>

      <Separator />

      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        <dt className="text-muted-foreground">From</dt>
        <dd>
          {row.name} ({row.email})
        </dd>
        <dt className="text-muted-foreground">Organization</dt>
        <dd>
          {row.organizationId && row.orgName ? (
            <Link
              to="/admin/organizations/$orgId"
              params={{ orgId: row.organizationId }}
              className="underline underline-offset-4"
            >
              {row.orgName}
            </Link>
          ) : (
            <EmptyValue>Not signed in</EmptyValue>
          )}
        </dd>
        <dt className="text-muted-foreground">Sent from</dt>
        <dd>{row.pagePath ?? <EmptyValue>Unknown</EmptyValue>}</dd>
        <dt className="text-muted-foreground">IP</dt>
        <dd className="font-mono text-xs">
          {row.clientIp ?? <EmptyValue>Unknown</EmptyValue>}
        </dd>
        {row.handledByName ? (
          <>
            <dt className="text-muted-foreground">Handled by</dt>
            <dd>
              {row.handledByName}
              {row.handledAt ? ` · ${formatWhen(row.handledAt)}` : ''}
            </dd>
          </>
        ) : null}
      </dl>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium">Internal note</span>
        <Textarea
          rows={4}
          value={note}
          maxLength={2000}
          placeholder="Not sent to the customer. What was done, or what is still owed."
          onChange={(e) => setNote(e.target.value)}
        />
        <PendingButton
          type="button"
          variant="outline"
          size="sm"
          className="self-start"
          pending={saveNote.isPending}
          disabled={note === (row.internalNote ?? '')}
          onClick={() => saveNote.mutate()}
        >
          Save note
        </PendingButton>
      </div>
    </div>
  )
}

type StatusFilter = 'all' | 'new' | 'handled'
type TopicFilter = 'all' | SupportTopic

function AdminSupportPage() {
  const { data, isPending } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => listSupportMessages(),
  })
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('new')
  const [topicFilter, setTopicFilter] = useState<TopicFilter>('all')
  const [open, setOpen] = useState<SupportMessageRow | null>(null)

  const rows = useMemo(() => {
    if (!data) return undefined
    const q = search.trim().toLowerCase()
    return data.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false
      if (topicFilter !== 'all' && row.topic !== topicFilter) return false
      if (!q) return true
      return [row.name, row.email, row.message, row.orgName ?? '']
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
  }, [data, search, statusFilter, topicFilter])

  const newCount = data?.filter((r) => r.status === 'new').length ?? 0

  const columns = useMemo<ColumnDef<SupportMessageRow>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Received',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs">
            {formatReceived(row.original.createdAt)}
          </span>
        ),
      },
      // Status and topic share a cell: two short badges cost less width than two
      // columns with their own headers, and the Open action has to stay on
      // screen without a horizontal scroll.
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1.5">
            <StatusBadge status={row.original.status} />
            <Badge variant="outline">
              {SUPPORT_TOPIC_LABELS[row.original.topic]}
            </Badge>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'From',
        cell: ({ row }) => (
          <div className="flex max-w-[12rem] flex-col">
            <span className="truncate">{row.original.name}</span>
            <span className="truncate text-muted-foreground text-xs">
              {row.original.email}
            </span>
          </div>
        ),
      },
      {
        id: 'org',
        header: 'Organization',
        cell: ({ row }) =>
          row.original.organizationId && row.original.orgName ? (
            <Link
              to="/admin/organizations/$orgId"
              params={{ orgId: row.original.organizationId }}
              className="block max-w-[12rem] truncate hover:underline"
            >
              {row.original.orgName}
            </Link>
          ) : (
            <EmptyValue>Anonymous</EmptyValue>
          ),
      },
      {
        id: 'message',
        header: 'Message',
        cell: ({ row }) => (
          <span className="line-clamp-2 max-w-[18rem] text-muted-foreground text-xs">
            {row.original.message}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen(row.original)}
          >
            Open
          </Button>
        ),
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Support"
        description="Messages from the /support form. Replies go out from the shared support mailbox, not from here."
      />

      <Card>
        <CardHeader>
          <CardTitle>Inbox</CardTitle>
          <CardDescription>
            {isPending
              ? 'Loading…'
              : `${newCount} new of ${data?.length ?? 0} total`}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, organization, message"
              className="max-w-xs"
            />
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <SelectTrigger className="w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="handled">Handled</SelectItem>
                <SelectItem value="all">All statuses</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={topicFilter}
              onValueChange={(v) => setTopicFilter(v as TopicFilter)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All topics</SelectItem>
                {SUPPORT_TOPICS.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DataTable
            columns={columns}
            data={rows}
            isPending={isPending}
            pageSize={25}
            emptyMessage="Nothing here. With the status filter on New, that means everything has been handled."
            initialSorting={[{ id: 'createdAt', desc: true }]}
          />
        </CardContent>
      </Card>

      <Sheet
        open={open !== null}
        onOpenChange={(next) => {
          if (!next) setOpen(null)
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Support request</SheetTitle>
            <SheetDescription>
              {open ? `${open.name} · ${open.email}` : null}
            </SheetDescription>
          </SheetHeader>
          {open ? (
            <div className="px-4 pb-8">
              <MessageDetail row={open} onClose={() => setOpen(null)} />
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  )
}
